/**
 * Hono HTTP layer types.
 *
 * `NexusEnv` is the environment generic passed to `Hono<NexusEnv>()` and every
 * `Context<NexusEnv>`. Variables set via `c.set(key, value)` are type-checked
 * against this shape.
 */

import type { Context, Env, MiddlewareHandler } from 'hono'
import { OpenAPIHono } from '@hono/zod-openapi'
import type { AppAbility } from '@kernel/abilities/ability.types.js'
import type { AuthUserRecord } from '../modules/auth/auth.types.js'

export type NexusEnv = {
  Variables: {
    /** Authenticated user (set by auth middleware). Undefined on anonymous routes. */
    user?: AuthUserRecord
    /** CASL ability built from user's roles. Always set when auth middleware runs (even anonymous → empty ability). */
    ability?: AppAbility
    /** Per-request UUID. Always set by request-id middleware. */
    requestId: string
    /** Propagated correlation ID (falls back to requestId). */
    correlationId: string
    /** Tenant ID resolved from token/header. Null for single-tenant. */
    tenantId: string | null
    /** PAT token ID when request authenticated via PAT. */
    patTokenId?: string
    /** Custom JWT claims added by onTokenCreate hook. */
    claims?: Record<string, unknown>
    /** Impersonation context when an admin acts on behalf of another user. */
    impersonation?: {
      originalUserId: string
      isImpersonating: boolean
    }
  }
}

export type HonoApp = OpenAPIHono<NexusEnv>
export type NexusContext = Context<NexusEnv>
export type NexusMiddleware = MiddlewareHandler<NexusEnv>

/**
 * Handler cast helper for `app.openapi()`.
 *
 * `@hono/zod-openapi` infers a strict `TypedResponse` return type from the
 * route schema, but our controllers return generic `Response` objects via
 * `c.json(...)`. The runtime behaviour is identical — this alias bridges
 * the static gap in one place so every migrated module uses the same shape.
 */
export type OpenApiHandler<E extends Env = NexusEnv> =
  (c: Context<E>) => Promise<any> | any

/** Factory alias to keep signatures terse in routes. */
export const createHonoRouter = (): OpenAPIHono<NexusEnv> => new OpenAPIHono<NexusEnv>()
